import { SubmeshData, SubmeshFeature } from '@kb3d/models/classes/features/submesh-feature';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { UVMapHelper } from '@kb3d/view/helpers/uvmap-helper';
import { eUvMode } from '@kb3d/models/classes/enums';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';

const uvHelper = new UVMapHelper();

// This is passed to the worker as additional information needed
export type SubmeshWorkerPayload = {
    multiMaterialIndex?: number;
};

// This is processed and deserialized in the worker based on the worker payload
export type SubmeshDependencyData = { multiMaterialIndex?: number; };

export class SubmeshGeometryProcessor extends FeatureGeometryProcessor<SubmeshFeature, SubmeshDependencyData> {
    readWorkerPayload(_workerHost: WorkerProcessorHost, data: SubmeshWorkerPayload) {
        return data as SubmeshDependencyData;
    }

    updateMeshGeometry(
        feature: SubmeshFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<SubmeshDependencyData>,
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges,dependencyData);
        const data = dependencyData.data;
        if (!data) {
            return newVertexData;
        }

        if (!newVertexData.submeshData) {
            newVertexData.submeshData = [];
        }

        if (data.multiMaterialIndex) {
            const verticesCount = (vertexDataChanges.positions || newVertexData.positions!).length / 3;
            const indicesCount = (vertexDataChanges.indices || newVertexData.indices!).length;

            for (let i = 0; i < feature.vertexGroups.length; i++) {
                const vertexGroup = feature.vertexGroups[i];

                const newSubmesh: SubmeshData = {
                    materialIndex: data.multiMaterialIndex,
                    verticesStart: 0,
                    verticesCount: verticesCount,
                    indexStart: vertexGroup.startIndex,
                    indexCount: Math.min(vertexGroup.endIndex, indicesCount) - vertexGroup.startIndex,
                };

                const existingIndex = newVertexData.submeshData.findIndex(
                    existingSubmesh =>
                        existingSubmesh.materialIndex === newSubmesh.materialIndex &&
                        existingSubmesh.verticesStart === newSubmesh.verticesStart &&
                        existingSubmesh.verticesCount === newSubmesh.verticesCount &&
                        existingSubmesh.indexStart === newSubmesh.indexStart &&
                        existingSubmesh.indexCount === newSubmesh.indexCount
                );

                if (existingIndex >= 0) {
                    newVertexData.submeshData.splice(existingIndex, 1);
                }

                newVertexData.submeshData.push(newSubmesh);
            }
        }

        // Calculate UV maps if that is enabled on this feature
        if (feature.uvMapping) {
            const uvMapVertexes: number[] = [];

            for (let i = 0; i < feature.vertexGroups.length; i++) {
                const vertexGroup = feature.vertexGroups[i];

                for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                    uvMapVertexes.push(i);
                }
            }

            if (uvMapVertexes.length > 0) {
                let existingUvs: number[] = [];
                if (newVertexData.uvs) {
                    existingUvs = [...newVertexData.uvs];
                }

                uvHelper.generateUVMapInPlace(newVertexData, feature, uvMapVertexes);

                // for projection mode "use existing", only apply the new uvs to the selected faces of the submesh
                if (feature.mode === eUvMode.useExisting) {
                    feature.vertexGroups.forEach(vertexGroup => {
                        for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                            if (newVertexData.indices && newVertexData.uvs) {
                                existingUvs[newVertexData.indices[i] * 2] =
                                    newVertexData.uvs[newVertexData.indices[i] * 2];
                                existingUvs[newVertexData.indices[i] * 2 + 1] =
                                    newVertexData.uvs[newVertexData.indices[i] * 2 + 1];
                            }
                        }
                    });
                    newVertexData.uvs = existingUvs;
                }
            }
        }

        return newVertexData;
    }
}
